import React, { useState, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import InputField from '../../components/forms/InputField';
import LoaderModal from '../../components/modals/loaderModal';
import { useDispatch } from 'react-redux';
import { registerAction, uploadImageAction } from '../../redux/actions/authAction';
import { errorToast, successToast } from '../../components/toast';
import RadioField from '../forms/radioInput';

const RegistrationModal = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const imageInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [enableGST, setEnableGST] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string().required('Store name is required'),
        mobile: Yup.string()
            .required('Mobile is required')
            .matches(/^[0-9]{10}$/, 'Mobile must be a 10-digit number'),
        email: Yup.string().email('Invalid email').required('Email is required'),
        address: Yup.string().required('Required!'),
        state: Yup.string().required('Required!'),
        city: Yup.string().required('Required!'),
        password: Yup.string()
            .required('Required!')
            .min(6, 'Password must be at least 6 characters'),
        confirm_password: Yup.string()
            .required('Required!')
            .oneOf([Yup.ref('password'), null], 'Passwords must match'),
        pincode: Yup.string()
            .required('Pincode is required')
            .matches(/^[0-9]{6}$/, 'Pincode must be a 6-digit number'),
        default_tax: enableGST ? Yup.string().required('Required!') : "",
        gst_number: enableGST ? Yup.string().required('Required!') : "",
        business_type: Yup.string().required('Business type is required'),

        // tax_with_product: Yup.string(),
    });

    const initialValues = {
        name: '',
        mobile: '',
        email: '',
        address: '',
        state: '',
        city: '',
        pincode: '',
        password: '',
        confirm_password: '',
        logo: null,
        gstEnabled: false,
        default_tax: null,
        gst_number: '',
        tax_with_product: "inclusive",
        transaction_in: "intraState",
        printPreference: "A4",
        fssaiNumber: "",
        business_type: "general",
    };

    const handleFormSubmit = (values) => {
        console.log('Form values:', values);
        setLoading(true);
        dispatch(registerAction(values, onSuccess, onError));
    };

    const handleImageChange = (event, setFieldValue) => {
        const file = event.target.files[0];
        if (!file) return;
        setLoading(true);
        const imageUploadSuccess = (data) => {
            setImagePreview(data.data.data);
            setFieldValue('logo', data.data.data);
            setLoading(false);
        };
        dispatch(uploadImageAction(file, imageUploadSuccess, onError));
    };

    const handleRemoveImage = (setFieldValue) => {
        setImagePreview(null);
        setFieldValue('logo', null);
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
        }
    };

    const onSuccess = (data) => {
        successToast(data.data.message);
        setLoading(false);
        onClose();
    };

    const onError = (error) => {
        const errorMsg = error.data?.message || 'Something went wrong!';
        errorToast(errorMsg);
        setLoading(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 text-black">
            <div className="bg-white w-[80%] max-w-4xl rounded-md shadow-md overflow-y-auto max-h-[90%] relative">
                <div className="sticky top-0 bg-white shadow p-4 z-10">
                    <h2 className="text-2xl font-bold">Register Your Account</h2>
                </div>

                <div className="p-6">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ setFieldValue, values }) => (
                            <Form className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <InputField
                                    value={values.name}
                                    label="Store Name"
                                    name="name"
                                    type="text"
                                    placeholder="Enter store name"
                                />
                                <InputField
                                    value={values.mobile}
                                    label="Contact"
                                    name="mobile"
                                    type="text"
                                    placeholder="Enter mobile number"
                                />
                                <InputField
                                    value={values.email}
                                    label="Email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter email address"
                                />
                                <InputField
                                    value={values.address}
                                    label="Address"
                                    name="address"
                                    type="text"
                                    placeholder="Enter address"
                                />
                                <InputField
                                    value={values.state}
                                    label="State"
                                    name="state"
                                    type="text"
                                    placeholder="Enter state"
                                />
                                <InputField
                                    value={values.city}
                                    label="City"
                                    name="city"
                                    type="text"
                                    placeholder="Enter city"
                                />
                                <InputField
                                    value={values.pincode}
                                    label="Pincode"
                                    name="pincode"
                                    type="text"
                                    placeholder="Enter pincode"
                                />

                                {/* Logo Upload */}
                                <div>
                                    <label className="block text-start">Logo (Optional)</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageChange(e, setFieldValue)}
                                        className="px-3 py-1.5 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                        ref={imageInputRef}
                                    />
                                    {imagePreview && (
                                        <div className="mt-3 flex items-center gap-4">
                                            <img
                                                src={imagePreview}
                                                alt="Logo Preview"
                                                className="h-20 w-20 object-cover rounded"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(setFieldValue)}
                                                className="text-red-500 underline text-sm"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <InputField
                                    value={values.password}
                                    label="Password"
                                    name="password"
                                    type="password"
                                    placeholder="Enter password"
                                />

                                <InputField
                                    value={values.confirm_password}
                                    label="Confirm Password"
                                    name="confirm_password"
                                    type="password"
                                    placeholder="Enter password"
                                />

                                <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                    <RadioField
                                        label="Business Type"
                                        name="business_type"
                                        options={[
                                            { label: "Restaurant", value: "restaurant" },
                                            { label: "General", value: "general" },
                                        ]}
                                        direction="row" // or "col" for vertical layout

                                    />
                                </div>

                                <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                    <RadioField
                                        label="Print Preference"
                                        name="printPreference"
                                        options={[
                                            { label: "A4", value: "A4" },
                                            { label: "Landscape", value: "landscape" },
                                            { label: "Thermal Print", value: "thermal" },
                                        ]}
                                        direction="row" // or "col" for vertical layout
                                    />
                                </div>

                                <InputField
                                    value={values.fssaiNumber}
                                    label="FSSAI No (Optional)"
                                    name="fssaiNumber"
                                    type="text"
                                    placeholder="Enter Fssai Lic No"
                                />
                                {/* GST Configuration */}
                                <div className="col-span-full">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={enableGST}
                                            onChange={(e) => {
                                                setEnableGST(e.target.checked);
                                                setFieldValue('gstEnabled', e.target.checked);
                                            }}
                                            className="mr-2"
                                        />
                                        Enable GST
                                    </label>
                                </div>

                                {enableGST && (
                                    <>
                                        <InputField
                                            value={values.default_tax}
                                            label="Default Tax Rate (%)"
                                            name="default_tax"
                                            type="number"
                                            placeholder="Enter default tax rate"
                                        />
                                        <InputField
                                            value={values.gst_number}
                                            label="GST Number"
                                            name="gst_number"
                                            type="text"
                                            placeholder="Enter GST number"
                                        />
                                        <div>
                                            <RadioField
                                                label="Tax With Product"
                                                name="tax_with_product"
                                                options={[
                                                    { label: "Inclusive Tax", value: "inclusive" },
                                                    { label: "Exclusive Tax", value: "exclusive" },
                                                ]}
                                                direction="row" // or "col" for vertical layout
                                            />
                                        </div>
                                        {/* <div>
                                            <RadioField
                                                label="Transaction Type"
                                                name="transaction_in"
                                                options={[
                                                    { label: "Intra-State", value: "intraState" },
                                                    { label: "Inter-State", value: "interState" },
                                                ]}
                                                direction="row" // or "col" for vertical layout
                                            />
                                        </div> */}
                                    </>
                                )}

                                <div className="col-span-full flex justify-between gap-4 sticky bottom-0 pb-1 bg-white">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                    >
                                        Register
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>

                {loading && <LoaderModal />}
            </div>
        </div>
    );
};

export default RegistrationModal;
